/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import Skeleton from 'react-loading-skeleton';
// import 'react-loading-skeleton/dist/skeleton.css';
import { userPermissionContext } from '../../../../app/context/UserPagePermission';
import { AsideMenuItem } from './AsideMenuItem';
import { AsideMenuItemWithSub } from './AsideMenuItemWithSub';

const DASHBOARD_LINK = '/dashboard';

/**
 * How well a menu link fits a page: 0 when it does not, higher is a closer fit.
 * A link fits its own page, the pages named after it ("/po" fits "/po-details") and, when
 * it is a plural, the pages named after its singular ("/sales-orders" fits "/sales-order-create").
 */
const linkFit = (page: string, link: string): number => {
	if (page === link) {
		return Infinity;
	}
	if (page.startsWith(link + '-')) {
		return link.length;
	}

	const singular = link.endsWith('s') ? link.slice(0, -1) : '';
	if (singular !== '' && (page === singular || page.startsWith(singular + '-'))) {
		return singular.length;
	}

	return 0;
};

/**
 * The menu link the current page belongs to, or null when none fits. The closest fit wins,
 * so "/purchase-bill" is not also "/purchase" and "/warehouse-transfers" not also "/warehouse".
 */
export const findMenuLink = (pathname: string, links: string[]): string | null => {
	// Menu links are one path segment: "/po-details/5" is the page "/po-details".
	const page = '/' + (pathname.split('/')[1] || '');

	return links.reduce<{ link: string | null, fit: number }>((best, link) => {
		const fit = linkFit(page, link);
		return fit > best.fit ? { link, fit } : best;
	}, { link: null, fit: 0 }).link;
};

export function AsideMenuMain() {

	const { pageList, loading } = useContext(userPermissionContext);
	const { pathname } = useLocation();
	// The menu that was open on the last page the menu knows.
	const lastOpenMenu = useRef<string | null>(null);

	// A menu with a single sub-menu is shown as that page itself, not as a menu of one.
	const menus = pageList.map((item: any) => {
		const subMenu = item.subMenu || [];
		return subMenu.length === 1
			? { ...subMenu[0], iconPath: subMenu[0].iconPath || item.iconPath, subMenu: [] }
			: { ...item, subMenu };
	});

	// Every page link in the menu => the menu it sits in (null at the top level).
	const menuOfPage: Record<string, string | null> = { [DASHBOARD_LINK]: null };
	menus.forEach((menu: any) => {
		if (menu.subMenu.length === 0) {
			menuOfPage[`/${menu.pagesLink}`] = null;
		}
		menu.subMenu.forEach((page: any) => {
			menuOfPage[`/${page.pagesLink}`] = `/${menu.pagesLink}`;
		});
	});

	const activeLink = findMenuLink(pathname, Object.keys(menuOfPage));
	const menuLinks = menus.filter((menu: any) => menu.subMenu.length > 0).map((menu: any) => `/${menu.pagesLink}`);
	const menuByName = findMenuLink(pathname, menuLinks);

	// The open menu is the one holding the current page. A page that fits no link but is
	// named after a menu ("/accesscontrol-pagemanagement") opens that menu, and a page the
	// menu does not know at all ("/my-profile") leaves the menu as it was.
	let openMenu = lastOpenMenu.current;
	if (activeLink !== null) {
		openMenu = menuOfPage[activeLink];
	} else if (menuByName !== null) {
		openMenu = menuByName;
	}

	useEffect(() => {
		lastOpenMenu.current = openMenu;
	}, [openMenu]);

	// The menu keeps its scroll position between pages; only bring the current page's link
	// into view when it is outside it (first load, or a link clicked inside a page).
	useEffect(() => {
		const wrapper = document.getElementById('kt_aside_menu_wrapper');
		const link = wrapper ? wrapper.querySelector<HTMLElement>('.menu-link.active') : null;
		if (!wrapper || !link || link.offsetParent === null) {
			return;
		}

		const top = link.getBoundingClientRect().top - wrapper.getBoundingClientRect().top;
		const below = top + link.offsetHeight - wrapper.clientHeight;
		if (top < 0) {
			wrapper.scrollTop += top;
		} else if (below > 0) {
			wrapper.scrollTop += below;
		}
	}, [activeLink]);

	return (
		<>
			{!loading &&
				<AsideMenuItem
					to={DASHBOARD_LINK}
					icon='/media/icons/duotune/abstract/abs014.svg'
					title={'Dashboard'}
					active={activeLink === DASHBOARD_LINK}
				/>
			}
			{menus.map((menu: any) => {
				const menuLink = `/${menu.pagesLink}`;

				if (menu.subMenu.length === 0) {
					return <AsideMenuItem to={menuLink} icon={menu.iconPath} title={menu.pagesTitle} active={activeLink === menuLink} key={menu.idPages} />;
				}

				return (
					<AsideMenuItemWithSub
						to={menuLink}
						icon={menu.iconPath}
						title={menu.pagesTitle}
						active={activeLink !== null && menuOfPage[activeLink] === menuLink}
						open={openMenu === menuLink}
						key={menu.idPages}
					>
						{menu.subMenu.map((page: any) => (
							<AsideMenuItem to={`/${page.pagesLink}`} title={page.pagesTitle} icon={page.iconPath} active={activeLink === `/${page.pagesLink}`} key={page.idPages} />
						))}
					</AsideMenuItemWithSub>
				);
			})}

		</>

	)
}
